import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PaymentStatusBadge } from "@/components/PaymentStatusBadge";
import { formatINR, formatDateTime } from "@/lib/format";
import { Loader2, CheckCircle2, XCircle, Ban } from "lucide-react";

export default function RepayResult() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const mon = sp.get("order");
  const [data, setData] = useState(null);
  const [polls, setPolls] = useState(0);

  const check = useCallback(async () => {
    if (!mon) return null;
    try {
      const { data } = await api.get(`/repay/online/status/${mon}`);
      setData(data);
      return data.status;
    } catch { return null; }
  }, [mon]);

  useEffect(() => {
    let timer; let cancelled = false;
    (async () => {
      const status = await check();
      if (cancelled) return;
      if (["Pending", "Processing"].includes(status) && polls < 20) timer = setTimeout(() => setPolls((p) => p + 1), 2000);
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [check, polls]);

  const status = data?.status;
  const paid = status === "Paid";
  const bad = ["Failed", "Cancelled", "Expired"].includes(status);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 h-16 flex items-center px-4 sm:px-6 border-b border-border bg-white/90 backdrop-blur-md"><Logo /></header>
      <main className="max-w-md mx-auto px-4 py-12">
        <Card className="p-8 ip-card-shadow text-center" data-testid="repay-result-card">
          {!status && <Loader2 className="h-10 w-10 animate-spin text-brand mx-auto" />}
          {paid && <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-emerald-50 text-emerald-600 mb-3"><CheckCircle2 className="h-9 w-9" /></div>}
          {bad && status === "Cancelled" && <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-amber-50 text-amber-600 mb-3"><Ban className="h-9 w-9" /></div>}
          {bad && status !== "Cancelled" && <div className="mx-auto h-16 w-16 grid place-items-center rounded-full bg-rose-50 text-rose-600 mb-3"><XCircle className="h-9 w-9" /></div>}
          {!paid && !bad && status && <Loader2 className="h-10 w-10 animate-spin text-brand mx-auto mb-2" />}
          <h1 className="text-xl font-extrabold tracking-tight mt-3">
            {paid ? "Repayment successful" : bad ? `Payment ${status.toLowerCase()}` : "Confirming your payment…"}
          </h1>
          {data && (
            <>
              <div className="mt-3"><PaymentStatusBadge status={status} /></div>
              <p className="mt-4 font-mono text-2xl font-extrabold text-brand">{formatINR(data.amount)}</p>
              <p className="text-xs text-muted-foreground mt-1">Loan repayment</p>
              {data.provider_reference && <p className="text-sm mt-3">Reference: <span className="font-mono">{data.provider_reference}</span></p>}
              {data.paid_at && <p className="text-xs text-muted-foreground mt-1">{formatDateTime(data.paid_at)}</p>}
            </>
          )}
          {!paid && !bad && <p className="text-sm text-muted-foreground mt-4">We verify every payment securely on our servers. This may take a few moments.</p>}
          <Button onClick={() => nav("/")} className="mt-6 w-full h-11" data-testid="repay-result-back">Back to Dashboard</Button>
        </Card>
      </main>
    </div>
  );
}
